import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Loader from "@amzn/meridian/esm/components/loader";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import errorTokens from "@amzn/meridian-tokens/base/icon/error";
import {
  AIMS_STATUS,
  MAIN_CONTEXT,
  PANEL_WIDTH,
  ROLES,
  STATUS_BUTTON_MIN_WIDTH,
  TRANSCRIPT_NOT_AVAILABLE_MESSAGE,
  TRANSCRIPTION_STATES,
  WAVEFORM_ICON_SCALE,
} from "../../constants";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";
import NotAvailable from "../../components/NotAvailable";
import WaveformLayout from "../../components/waveformLayout/WaveformLayout";
import initialImage from "../../images/attendee_initial.svg";
import stoppedImage from "../../images/progress_stop_image.svg";
import { scaleIconClass } from "../../styles/icon.styles";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import { openUrl } from "../../utils/zoomSdkUtils";
import { useMeeting } from "../../context/MeetingContext";
import { useHireTranscription } from "./hire.hooks";
import { END_BUTTON_TOKENS, HIRE_TEXT, HIRE_URL } from "./hire.constants";

/**
 * Hire - in-meeting UI for HIRE transcript meetings. Separate from the standard UI: no Assistant,
 * Summaries, Settings, or footer. Only organizers / alternative hosts in an eligible meeting can transcribe.
 */
const Hire = () => {
  const { aimsStatus, role, meetingId, meetingUuid, alias, appContext, autoStartMode, isAutostarting, leoInterviewer, initialSession } = useMeeting();
  const session = useHireTranscription({
    role,
    meetingId,
    meetingUuid,
    alias,
    appContext,
    autoStartMode,
    initialTranscriptionState: initialSession.transcriptionState,
    initialIsRtmsStartedByOther: initialSession.isRtmsStartedByOther,
    initialRtmsRequesterName: initialSession.rtmsRequesterName,
    initialIsAutostarting: isAutostarting,
    leoInterviewer,
  });
  const canTranscribe = aimsStatus === AIMS_STATUS.TRANSCRIPT_AVAILABLE
    && (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST);
  const state = session.transcriptionState;

  return (
    <Theme palette={aimsTheme}>
      <Column alignmentHorizontal="center" spacing="none">
        <Box width={PANEL_WIDTH}>
          {!canTranscribe ? (
            // Not an organizer / alternative host, or the meeting isn't eligible
            <NotAvailable title={HIRE_TEXT.NOT_AVAILABLE_TITLE} message={TRANSCRIPT_NOT_AVAILABLE_MESSAGE} />
          ) : state === TRANSCRIPTION_STATES.LOADING ? (
            // Starting or ending transcription
            <WaveformLayout icon={<Loader size="medium" />} />
          ) : state === TRANSCRIPTION_STATES.REJECTED ? (
            // Someone else is already starting transcription
            <WaveformLayout icon={<Icon className={scaleIconClass(WAVEFORM_ICON_SCALE)} tokens={errorTokens} color="error" />}>
              <Column spacing="none">
                <Text type="h50">{session.rtmsRequesterName}</Text>
                <Text type="b200">{HIRE_TEXT.REJECTED_SUFFIX}</Text>
              </Column>
            </WaveformLayout>
          ) : state === TRANSCRIPTION_STATES.TRANSCRIBING ? (
            // Red End button in the Start button's spot; hidden when someone else started the session
            <WaveformLayout>
              <Text type="h50">{HIRE_TEXT.TRANSCRIBING}</Text>
              {!session.isRtmsStartedByOther && (
                <Theme tokens={END_BUTTON_TOKENS}>
                  <Button onClick={session.end} minWidth={STATUS_BUTTON_MIN_WIDTH} size="small">End</Button>
                </Theme>
              )}
            </WaveformLayout>
          ) : state === TRANSCRIPTION_STATES.STOPPED ? (
            // Transcription ended; point to Hire for the transcript, and allow starting again
            <WaveformLayout image={stoppedImage}>
              <Column spacing="400">
                <Column spacing="300">
                  <Text type="h50">{HIRE_TEXT.STOPPED_TITLE}</Text>
                  <Text type="b200" color="secondary">{HIRE_TEXT.STOPPED_NOTES}</Text>
                  <Text type="b200" color="secondary">{HIRE_TEXT.STOPPED_TRANSCRIPT}</Text>
                </Column>
                <Text type="b200">
                  <Link onClick={() => openUrl(HIRE_URL)}>{HIRE_TEXT.GO_TO_HIRE}</Link>
                </Text>
              </Column>
              <Button onClick={session.start} minWidth={STATUS_BUTTON_MIN_WIDTH} size="small">Start</Button>
            </WaveformLayout>
          ) : (
            // Not started yet
            <WaveformLayout image={initialImage}>
              <Text type="h50">{HIRE_TEXT.TITLE}</Text>
              <Button onClick={session.start} minWidth={STATUS_BUTTON_MIN_WIDTH} size="small">Start</Button>
            </WaveformLayout>
          )}
        </Box>
      </Column>
      <AlertModal
        variant={ALERT_VARIANT.SERVER_NOT_RESPONDING}
        isOpen={session.isServerErrorOpen}
        onClose={session.closeServerError}
        context={MAIN_CONTEXT.TRANSCRIBE}
      />
      <AlertModal
        variant={ALERT_VARIANT.RTMS_ERROR}
        isOpen={session.rtmsErrorType !== null}
        errorType={session.rtmsErrorType}
        onCancel={session.cancelRtmsError}
        onRestart={session.restartAfterRtmsError}
        context={MAIN_CONTEXT.TRANSCRIBE}
      />
    </Theme>
  );
};

export default Hire;
